import { format } from 'date-fns'
import { COMMON_CURRENCIES, getCurrencySymbol } from '../redux/slices/currencySlice';

/**
 * Format a number as currency with the appropriate currency symbol and locale formatting
 * @param {number|string} amount - The amount to format
 * @param {string} currencyCode - The ISO 4217 currency code (e.g., USD, EUR)
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (amount, currencyCode = 'USD') => {
  // Handle various input scenarios
  if (amount === null || amount === undefined || amount === '') {
    return '';
  }

  // Parse as float if it's a string
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  // Handle non-numeric amounts
  if (isNaN(numericAmount)) {
    return '';
  }
  
  // Ensure we have a valid currency code
  let normalizedCurrencyCode = 'USD'; // Default
  
  if (currencyCode) {
    if (typeof currencyCode === 'string') {
      // Normalize to uppercase
      normalizedCurrencyCode = currencyCode.toUpperCase();
    } else {
      console.warn(`Invalid currency code format: ${currencyCode}, using USD`);
    }
  } else {
    console.warn('Currency code not provided, using USD');
  }
  
  // Add debug logging
  // console.log(`formatCurrency called with:`, { 
  //   amount, 
  //   numericAmount, 
  //   originalCurrency: currencyCode,
  //   normalizedCurrency: normalizedCurrencyCode
  // });
  
  // Check for zero amounts
  if (numericAmount === 0) {
    return `${getCurrencySymbol(normalizedCurrencyCode)}0`;
  }

  try {
    // Get the browser's locale for proper number formatting
    const userLocale = navigator.language || navigator.userLanguage || 'en-US';
    
    // Determine decimal places based on currency
    // Most currencies use 2 decimal places, but some (like JPY) use 0
    const decimalPlaces = ['JPY', 'KRW', 'VND'].includes(normalizedCurrencyCode) ? 0 : 2;
    
    // Use Intl.NumberFormat for locale-aware currency formatting
    const formatted = new Intl.NumberFormat(userLocale, {
      style: 'currency',
      currency: normalizedCurrencyCode,
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces
    }).format(numericAmount);
    
    // console.log(`formatCurrency result:`, formatted);
    return formatted;
  } catch (error) {
    console.error('Currency formatting error:', error);
    // Fallback to basic formatting with currency symbol if Intl.NumberFormat fails
    const symbol = getCurrencySymbol(normalizedCurrencyCode);
    return `${symbol}${numericAmount.toFixed(2)}`;
  }
}

export const formatAddress = (address) => {
  if (!address) return ''

  const {
    street1,
    street2,
    city,
    state,
    postal_code,
    country,
  } = address

  let formattedAddress = street1
  if (street2) formattedAddress += ', ' + street2
  formattedAddress += ', ' + city
  if (state) formattedAddress += ', ' + state
  if (postal_code) formattedAddress += ' ' + postal_code
  if (country) formattedAddress += ', ' + country

  return formattedAddress
}

export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return ''

  // Strip non-numeric characters
  const stripped = phoneNumber.replace(/\D/g, '')

  // Format as (XXX) XXX-XXXX for US/CA numbers
  if (stripped.length === 10) {
    return `(${stripped.slice(0, 3)}) ${stripped.slice(3, 6)}-${stripped.slice(
      6
    )}`
  }

  // For international numbers just add a space every 3 characters
  return stripped.replace(/(\d{3})(?=\d)/g, '$1 ')
}

export const formatDate = (dateString) => {
  if (!dateString) return ''
  
  try {
    const date = new Date(dateString)
    return format(date, 'PPP') // Format as "April 29, 2021"
  } catch (error) {
    console.error('Error formatting date:', error)
    return dateString
  }
}

export const formatTime = (dateString) => {
  if (!dateString) return ''
  
  try {
    const date = new Date(dateString)
    return format(date, 'p') // Format as "12:00 AM"
  } catch (error) {
    console.error('Error formatting time:', error)
    return ''
  }
}

export const formatDateTime = (dateString) => {
  if (!dateString) return ''
  
  try {
    const date = new Date(dateString)
    return format(date, 'PPp') // Format as "April 29, 2021 12:00 AM"
  } catch (error) {
    console.error('Error formatting date and time:', error)
    return dateString
  }
}

// Simple formatter for percentages
export const formatPercentage = (value) => {
  if (value === null || value === undefined) return '';
  
  const number = parseFloat(value);
  if (isNaN(number)) return '';
  
  return `${number.toFixed(1)}%`;
}

/**
 * Format a price with the proper currency symbol
 * @param {number} price - The price value
 * @param {string} currency - The currency code (USD, EUR, etc.)
 * @param {boolean} showCode - Whether to show the currency code (defaults to false)
 * @returns {string} - Formatted price with currency symbol
 */
export const formatCurrencyOld = (price, currency = 'USD', showCode = false) => {
  if (price === undefined || price === null) return '';
  
  // Default to USD if currency is not provided
  const currencyCode = currency || 'USD';
  
  // Find the currency in our common currencies list
  const currencyObj = COMMON_CURRENCIES.find(c => c.code === currencyCode) || {
    code: currencyCode,
    symbol: '$', // Default to $ symbol if currency is not found
    name: currencyCode
  };
  
  // Format the price with the currency symbol
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  
  const formattedPrice = formatter.format(price);
  
  // Different currencies have different conventions for symbol placement
  const rightSymbolCurrencies = ['CZK', 'SEK', 'DKK'];
  
  if (rightSymbolCurrencies.includes(currencyCode)) {
    return showCode 
      ? `${formattedPrice} ${currencyObj.symbol} ${currencyObj.code}`
      : `${formattedPrice} ${currencyObj.symbol}`;
  }
  
  return showCode 
    ? `${currencyObj.symbol}${formattedPrice} ${currencyObj.code}`
    : `${currencyObj.symbol}${formattedPrice}`;
};

/**
 * Format a date string or timestamp into a readable format
 * @param {string|number|Date} date - The date to format
 * @param {string} format - Format style ('short', 'medium', 'long', 'full')
 * @returns {string} - Formatted date string
 */
export const formatDateOld = (date, format = 'medium') => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  
  // Check if date is valid
  if (isNaN(dateObj.getTime())) return '';
  
  const options = {
    short: { month: 'numeric', day: 'numeric', year: '2-digit' },
    medium: { month: 'short', day: 'numeric', year: 'numeric' },
    long: { month: 'long', day: 'numeric', year: 'numeric' },
    full: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }
  };
  
  return new Intl.DateTimeFormat('en-US', options[format]).format(dateObj);
};

/**
 * Truncate text with ellipsis if it exceeds max length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} - Truncated text
 */
export const truncateText = (text, maxLength) => {
  if (!text || text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
};

/**
 * Format a rental period for display
 * @param {Object} period - The rental period object
 * @returns {string} - Formatted rental period
 */
export const formatRentalPeriod = (period) => {
  if (!period) return 'Unknown period';
  
  const number = period.number || 1;
  const option = period.option || 'day';
  
  return `${number} ${option}${number > 1 ? 's' : ''}`;
}; 